import type { UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";

import salesFetchService from "app/common/services/salesFetchService";

import { createAccountManagerApiService } from "../api/accountManagerApiService";
import type { AccountManagers } from "../types";

import { accountManagerQueryKeys } from "./queryKeys";

const FIVE_MINUTES = 5 * 60 * 1000;

const fetchAccountManagers = (): Promise<AccountManagers> =>
    createAccountManagerApiService(salesFetchService).fetchAccountManagers();

/**
 * Shared query options for the Salesforce Account Manager / Account Success
 * Manager fetch. Centralised so both the consuming hook and the on-entry
 * prefetch in `SalesIntelligenceRoot` use identical key, fetcher and stale
 * time, ensuring the cache hits across surfaces.
 */
export const accountManagersQueryOptions = {
    queryKey: accountManagerQueryKeys.all(),
    queryFn: fetchAccountManagers,
    staleTime: FIVE_MINUTES,
} as const;

/**
 * Resolves the Salesforce Account Manager (AM) and Account Success Manager
 * (ASM) for the current user along with their ChiliPiper personal-link slugs.
 * The result powers the "Book a meeting" CTA across Sales Intelligence.
 *
 * The query is shared across all callers — multiple components mounting the
 * hook on the same render dedupe to a single network request.
 */
export const useAccountManagersQuery = (): UseQueryResult<AccountManagers> => {
    return useQuery(accountManagersQueryOptions);
};

export const resolveChiliPiperSlug = (data: AccountManagers | undefined): string | null => {
    if (!data) {
        return null;
    }

    return data.accountManager?.chiliPiperSlug || data.accountSuccessManager?.chiliPiperSlug || null;
};

/**
 * Returns the slug used as the ChiliPiper FireRouter when the user clicks
 * "Book a meeting". Prefers the dedicated Account Manager and falls back to
 * the Account Success Manager when the AM slug is missing or empty. Returns
 * `null` while loading, on error, or when neither role yields a non-empty
 * slug, so the CTA stays hidden.
 */
export const useAccountManagerChiliPiperSlug = (): string | null => {
    const { data } = useAccountManagersQuery();

    return resolveChiliPiperSlug(data);
};
